import { customElement, property } from 'lit/decorators.js';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import { type Topology, type Objects } from 'topojson-specification';
// import { type GeoPath, type GeoPermissibleObjects, type GeoProjection } from 'd3';
import {
  type FeatureCollection,
  type MultiPoint,
  type Point,
  // type Feature,
} from 'geojson';
import _topologyOfWorld from './world-topo.json';

import { ChartBase } from './base';

type Property =
  (typeof _topologyOfWorld.objects.collection.geometries)[number]['properties'];

const topologyOfWorld = _topologyOfWorld as unknown as Topology<Objects<Property>>;

export const provinces = _topologyOfWorld.objects.collection.geometries.map(
  (p) => p.properties.name,
);

@customElement('d3-world-map')
export class WorldMap extends ChartBase {
  @property({ type: Number, attribute: 'center-longitude' })
  centerLongitude: number = 0;

  @property({ type: String, attribute: 'country-color' })
  countryColor: string = '#FFFFFF';

  @property({ type: String, attribute: 'border-color' })
  borderColor: string = '#AFAFAF';

  @property({ type: String, attribute: 'ocean-color' })
  oceanColor: string = '#1c2f9a';

  @property({ type: String, attribute: 'point-color' })
  pointColor: string = '#AF3127';

  @property({ type: Number, attribute: 'point-radius' })
  pointRadius: number = 5;

  @property({ type: Boolean, attribute: 'point-animate' })
  pointAnimate: boolean = true;

  @property({ type: Array, attribute: 'points' })
  points: [number, number][] = [];

  @property({ type: Number, attribute: 'line-dash-offset' })
  lineDashOffset: number = 5;

  @property({ type: Number, attribute: 'line-dash-animate' })
  lineDashAnimate: number = 0;

  @property({ type: Number, attribute: 'line-stroke-width' })
  lineStrokeWidth: number = Math.PI / Math.E;

  @property({ type: String, attribute: 'line-stroke' })
  lineStroke: string = 'darkblue';

  @property({ type: Array, attribute: 'lines' })
  lines: [[number, number], [number, number]][] = [];

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  override firstUpdated(changedProperties: Map<PropertyKey, unknown>) {
    super.firstUpdated(changedProperties);

    this.renderSvg();
  }

  override updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);

    this.updateSvg();
  }

  override attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value);
    this.requestUpdate();
  }

  renderSvg() {
    const projection = d3.geoEqualEarth().rotate([-this.centerLongitude, 0]);
    const path = d3.geoPath(projection);

    const world = topojson.feature<Property>(
      topologyOfWorld,
      topologyOfWorld.objects.collection,
    ) as FeatureCollection<Point | MultiPoint, Property>;

    this.svg?.append('g');

    this.svg
      ?.select('g')
      .append('path')
      .attr('class', 'ocean')
      .attr('d', path({ type: 'Sphere' }));

    this.svg
      ?.select('g')
      .selectAll('.country')
      .data(world.features)
      .enter()
      .append('path')
      .attr('class', 'country')
      .attr('d', path);
  }

  override resizeChart() {
    super.resizeChart();

    const { width } = getComputedStyle(this.chartContainer);
    const node = this.svg?.select('g').node() as SVGSVGElement;
    const bbox = node.getBBox();
    const ratio = (parseInt(width, 10) - bbox.x * 2) / (bbox.width - bbox.x);
    this.svg?.select('g').attr('transform', `scale(${ratio.toFixed(3)})`);
  }

  updateSvg() {
    const { width } = getComputedStyle(this.chartContainer);
    const node = this.svg?.select('g').node() as SVGSVGElement;
    const bbox = node.getBBox();
    const ratio = (parseInt(width, 10) - bbox.x * 2) / (bbox.width - bbox.x);
    const projection = d3.geoEqualEarth().rotate([-this.centerLongitude, 0]);
    const pathFn = d3.geoPath(projection);

    this.svg
      ?.select('g')
      ?.attr('transform', `scale(${ratio.toFixed(3)})`)
      ?.selectAll('.country')
      ?.attr('d', (s: any, obj, ...a) => pathFn(s, obj, ...a))
      ?.style('fill', this.countryColor)
      ?.style('stroke', this.borderColor);

    this.svg?.select('g').selectAll('.ocean').style('fill', this.oceanColor);

    this.svg?.select('g').selectAll('.point').remove();

    const points = this.svg
      ?.select('g')
      .selectAll('.point')
      .data(this.points)
      .enter()
      .append('circle')
      .attr('class', 'point')
      .attr('cx', ([x, y]) => {
        const p = projection([x, y]) ?? [x, y];
        return p[0];
      })
      .attr('cy', ([x, y]) => {
        const p = projection([x, y]) ?? [x, y];
        return p[1];
      })
      .attr('r', this.pointRadius)
      .attr('fill', this.pointColor);
    if (this.pointAnimate) {
      points
        ?.append('animate')
        .attr('attributeName', 'r')
        .attr(
          'values',
          `${this.pointRadius * 0.2};${this.pointRadius};${this.pointRadius * 2};${
            this.pointRadius
          };${this.pointRadius * 0.2}`,
        )
        .attr('dur', '2s')
        .attr('repeatCount', 'indefinite');
    }

    this.svg?.select('g').selectAll('.line').remove();

    const lines = this.svg
      ?.select('g')
      .selectAll('.line')
      .data(this.lines)
      .enter()
      .append('path')
      .attr('class', 'line')
      .attr(
        'd',
        d3
          .line()
          .x((p) => {
            const _p = projection(p) ?? [0, 0];
            return _p[0];
          })
          .y((p) => {
            const _p = projection(p) ?? [0, 0];
            return _p[1];
          })
          .curve(d3.curveCardinal.tension(0.5)),
      )
      .attr('stroke', this.lineStroke)
      .attr('stroke-width', this.lineStrokeWidth)
      .attr('stroke-dashoffset', 0)
      .attr('stroke-dasharray', this.lineDashOffset);
    if (this.lineDashAnimate > 0) {
      lines
        ?.append('animate')
        .attr('attributeName', 'stroke-dashoffset')
        .attr(
          'values',
          `-${this.lineDashOffset};-${this.lineDashOffset * 2};-${
            this.lineDashOffset * 3
          }`,
        )
        .attr('dur', `${this.lineDashAnimate}ms`)
        .attr('repeatCount', 'indefinite');
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd3-world-map': WorldMap;
  }
}
